import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import useBadgeToast from "@/components/site/BadgeToast";
import { Plus, Calendar, MapPin, Loader2, Search, Filter, X, Mail, Users, Instagram, ChevronDown, LogIn, Check, ArrowDown } from "lucide-react";
import PageBlocks from "@/components/site/PageBlocks";
import PageHeader from "@/components/site/PageHeader";
import EditableText from "@/components/site/EditableText";
import ReportButton from "@/components/site/ReportButton";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const TYPE_COLORS = {
  rassemblement: "#0047FF",
  manifestation: "#FF0054",
  blocage: "#FFD600",
  autre: "#000000",
};
const TYPE_LABELS = {
  rassemblement: "Rassemblement",
  manifestation: "Marche / Manifestation",
  blocage: "Blocage d'école",
  autre: "Autre",
};
const PROVINCES = ["Bruxelles-Capitale", "Liège", "Hainaut", "Namur", "Brabant wallon", "Luxembourg"];

function colorIcon(color) {
  return L.divIcon({
    html: `<div style="width:28px;height:28px;background:${color};border:2px solid #000;border-radius:50%;box-shadow:2px 2px 0 0 #000"></div>`,
    iconSize: [28, 28],
    className: "",
  });
}

function groupIcon() {
  return L.divIcon({
    html: `<div style="width:30px;height:30px;background:#7ED957;border:2px solid #000;border-radius:6px;box-shadow:2px 2px 0 0 #000;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:14px;color:#000;">★</div>`,
    iconSize: [30, 30],
    className: "",
  });
}

function FlyTo({ position }) {
  const map = useMap();
  React.useEffect(() => {
    if (position) map.flyTo(position, 13, { duration: 0.8 });
  }, [position, map]);
  return null;
}

export default function Carte() {
  const { user } = useAuth();
  const showBadges = useBadgeToast();
  const [searchParams] = useSearchParams();
  const [items, setItems] = React.useState([]);
  const [groups, setGroups] = React.useState([]);
  const [joined, setJoined] = React.useState(new Set());
  const [filterType, setFilterType] = React.useState("");
  const [filterProvince, setFilterProvince] = React.useState("");
  const [filterDate, setFilterDate] = React.useState("");
  const [filterSchool, setFilterSchool] = React.useState("");
  const [showGroupsOnMap, setShowGroupsOnMap] = React.useState(true);
  const [showInitiativesOnMap, setShowInitiativesOnMap] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [showGroupForm, setShowGroupForm] = React.useState(false);
  const [flyPos, setFlyPos] = React.useState(null);

  const wantsToCreate = searchParams.get("create") === "1";

  const load = React.useCallback(() => {
    api.get("/initiatives").then((r) => setItems(r.data)).catch(() => {});
    api.get("/local-groups").then((r) => setGroups(r.data)).catch(() => {});
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // Load joined set for connected user
  React.useEffect(() => {
    if (!user) { setJoined(new Set()); return; }
    api.get("/me/activity")
      .then((r) => setJoined(new Set((r.data.joined_initiatives || []).map((i) => i.id))))
      .catch(() => {});
  }, [user]);

  // Open form if ?create=1 AND user is logged in
  React.useEffect(() => {
    if (wantsToCreate && user) setShowForm(true);
  }, [wantsToCreate, user]);

  // Anchor to groupes if ?hash=groupes
  React.useEffect(() => {
    if (window.location.hash === "#groupes") {
      setTimeout(() => document.getElementById("groupes-section")?.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, []);

  const filtered = React.useMemo(() => items.filter((i) => {
    if (filterType && i.type !== filterType) return false;
    if (filterProvince && i.province !== filterProvince) return false;
    if (filterDate && i.date && i.date.slice(0, 10) < filterDate) return false;
    if (filterSchool && !(i.school || "").toLowerCase().includes(filterSchool.toLowerCase())) return false;
    return true;
  }), [items, filterType, filterProvince, filterDate, filterSchool]);

  const agendaItems = React.useMemo(() => [...filtered].sort((a, b) => (a.date || "").localeCompare(b.date || "")), [filtered]);

  const onCreateClick = () => {
    if (!user) {
      toast.error("Connexion requise pour créer une action. Inscris-toi en 30 secondes !");
      return;
    }
    setShowForm((s) => !s);
  };

  const toggleJoin = async (init) => {
    if (!user) {
      toast.error("Connecte-toi pour participer aux actions.");
      return;
    }
    try {
      if (joined.has(init.id)) {
        await api.delete(`/initiatives/${init.id}/leave`);
        setJoined((prev) => { const s = new Set(prev); s.delete(init.id); return s; });
        toast.success("Tu ne participes plus à cette action.");
      } else {
        await api.post(`/initiatives/${init.id}/join`);
        setJoined((prev) => new Set(prev).add(init.id));
        toast.success("Tu participes à cette action ! Retrouve-la dans ton profil.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const scrollTo = (id) => () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <PageHeader slug="carte" fallbackTitle="La [carte](pink) du mouvement" fallbackSubtitle="Visualise les rassemblements, manifestations et blocages près de chez toi." />
        </div>
      </div>

      {/* Quick anchors + Create action button (grouped) */}
      <div className="mt-4 flex flex-wrap gap-2 items-center" data-testid="carte-anchors">
        <button onClick={scrollTo("agenda-section")} className="neo-btn bg-cef-yellow text-sm py-2" data-testid="anchor-agenda"><ArrowDown className="w-4 h-4" /> Aller à l'agenda</button>
        <button onClick={scrollTo("groupes-section")} className="neo-btn bg-cef-green text-sm py-2" data-testid="anchor-groupes"><ArrowDown className="w-4 h-4" /> Groupes locaux</button>
        <button onClick={onCreateClick} className="neo-btn bg-cef-pink text-white text-sm py-2" data-testid="add-initiative-btn">
          <Plus className="w-4 h-4" /> Créer une action
        </button>
      </div>

      <PageBlocks slug="carte" className="mt-6" />

      {/* LOGIN PROMPT for creation */}
      {!user && wantsToCreate && (
        <div className="mt-6 neo-card p-5 bg-cef-yellow" data-testid="create-login-prompt">
          <div className="font-display font-black uppercase text-lg flex items-center gap-2"><LogIn className="w-5 h-5" /> Connexion requise</div>
          <p className="mt-2 text-sm">Pour publier une action sur la carte, tu dois être connecté·e. Crée ton compte en 30 secondes et débloque le badge <strong>Initiative</strong>.</p>
          <div className="mt-3 flex gap-2">
            <Link to="/signup?next=/carte%3Fcreate%3D1" className="neo-btn bg-cef-pink text-white" data-testid="prompt-signup">Créer un compte</Link>
            <Link to="/login?next=/carte%3Fcreate%3D1" className="neo-btn bg-white" data-testid="prompt-login">Se connecter</Link>
          </div>
        </div>
      )}

      {/* FILTERS */}
      <div className="mt-6 neo-card p-4 bg-white grid gap-3" data-testid="carte-filters">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-tight"><Filter className="w-4 h-4" /> Filtres</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
          <select className="neo-input" value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="filter-type">
            <option value="">Tous types</option>
            {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select className="neo-input" value={filterProvince} onChange={(e) => setFilterProvince(e.target.value)} data-testid="filter-province">
            <option value="">Toutes provinces</option>
            {PROVINCES.map((p) => <option key={p}>{p}</option>)}
          </select>
          <input className="neo-input" type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} data-testid="filter-date" />
          <SchoolAutocomplete value={filterSchool} onChange={setFilterSchool} />
        </div>
        <div className="flex flex-wrap gap-3 pt-2 border-t-2 border-black/10 text-sm font-bold">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={showInitiativesOnMap} onChange={(e) => setShowInitiativesOnMap(e.target.checked)} data-testid="filter-show-initiatives" />
            Actions / initiatives
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={showGroupsOnMap} onChange={(e) => setShowGroupsOnMap(e.target.checked)} data-testid="filter-show-groups" />
            Groupes locaux ({groups.length})
          </label>
        </div>
      </div>

      {showForm && user && (
        <CreateInitiativeForm
          user={user}
          onClose={() => setShowForm(false)}
          onCreated={(payload) => {
            if (payload.new_badges?.length) showBadges(payload.new_badges);
            if (payload.initiative?.lat) setFlyPos([payload.initiative.lat, payload.initiative.lng]);
            load();
          }}
        />
      )}

      {/* MAP */}
      <div className="mt-6 h-[520px] border-2 border-black rounded-md overflow-hidden shadow-brutal" data-testid="map-container">
        <MapContainer center={[50.6, 4.7]} zoom={8} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FlyTo position={flyPos} />
          {showInitiativesOnMap && filtered.filter((i) => i.lat && i.lng).map((i) => (
            <Marker key={i.id} position={[i.lat, i.lng]} icon={colorIcon(TYPE_COLORS[i.type] || "#000")}>
              <Popup>
                <div className="font-bold">{i.name}</div>
                <div className="text-xs text-neutral-600">{i.school ? `${i.school} · ` : ""}{i.city}{i.date ? ` · ${i.date.slice(0, 10)}` : ""}</div>
                <div className="mt-1 text-sm">{i.description}</div>
              </Popup>
            </Marker>
          ))}
          {showGroupsOnMap && groups.filter((g) => g.lat && g.lng).map((g) => (
            <Marker key={`grp-${g.id}`} position={[g.lat, g.lng]} icon={groupIcon()}>
              <Popup>
                <div className="font-bold inline-flex items-center gap-1"><Users className="w-3 h-3" /> {g.name}</div>
                {g.city && <div className="text-xs text-neutral-600">{g.city}{g.province ? ` · ${g.province}` : ""}</div>}
                {g.description && <div className="mt-1 text-sm">{g.description}</div>}
                {g.instagram_url && <a href={g.instagram_url} target="_blank" rel="noreferrer" className="text-xs font-bold underline mt-1 inline-block">Instagram ↗</a>}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      <div className="mt-4 flex flex-wrap gap-3 text-sm">
        {Object.entries(TYPE_COLORS).map(([k, c]) => (
          <div key={k} className="inline-flex items-center gap-2"><span className="w-3 h-3 border-2 border-black rounded-full" style={{ background: c }} /> {TYPE_LABELS[k]}</div>
        ))}
        <div className="inline-flex items-center gap-2"><span className="w-3 h-3 border-2 border-black bg-cef-green rounded-sm" /> Groupe local</div>
      </div>

      {/* AGENDA — expandable cards with "Participer" + "Contacter" */}
      <section id="agenda-section" className="mt-12 scroll-mt-24" data-testid="agenda-section">
        <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter uppercase flex items-center gap-2 mb-4">
          <Calendar className="w-7 h-7" strokeWidth={2.5} /> Agenda
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="agenda-list">
          {agendaItems.length === 0 && <div className="neo-card p-5 bg-white text-neutral-500 sm:col-span-2 lg:col-span-3">Aucune action ne correspond à ces filtres.</div>}
          {agendaItems.map((i) => (
            <AgendaCard
              key={i.id}
              item={i}
              joined={joined.has(i.id)}
              onToggleJoin={() => toggleJoin(i)}
              onFly={() => i.lat && setFlyPos([i.lat, i.lng])}
              canParticipate={!!user}
            />
          ))}
        </div>
      </section>

      {/* GROUPES LOCAUX */}
      <section id="groupes-section" className="mt-12 scroll-mt-24" data-testid="groupes-section">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-2">
          <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter uppercase flex items-center gap-2">
            <Users className="w-7 h-7" strokeWidth={2.5} /> Groupes d'élèves locaux
          </h2>
          <button
            onClick={() => {
              if (!user) {
                toast.error("Connecte-toi pour suggérer un groupe local.");
                return;
              }
              setShowGroupForm(true);
            }}
            className="neo-btn bg-cef-blue text-white text-sm py-2"
            data-testid="suggest-group-btn"
          >
            <Plus className="w-4 h-4" /> Suggérer un groupe
          </button>
        </div>
        <EditableText
          slug="carte"
          sectionKey="groupes-intro"
          className="text-neutral-600 max-w-2xl mb-4 [&_p]:m-0"
          as="div"
        >
          <p>Rejoins une antenne du CEF dans ta province. Chaque groupe organise ses propres actions et tu peux les suivre sur Instagram.</p>
        </EditableText>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="groupes-list">
          {groups.length === 0 && <div className="neo-card p-5 bg-white text-neutral-500 sm:col-span-2 lg:col-span-3">Aucun groupe local pour le moment.</div>}
          {groups.map((g) => (
            <div key={g.id} className="neo-card p-5 bg-white" data-testid={`groupe-${g.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-black text-lg tracking-tight">{g.name}</h3>
                {g.province && <span className="chip bg-cef-green text-[10px] py-0.5">{g.province}</span>}
              </div>
              {(g.city || g.school) && <div className="mt-1 text-xs text-neutral-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {[g.city, g.school].filter(Boolean).join(" · ")}</div>}
              {g.description && <p className="mt-2 text-sm text-neutral-700 line-clamp-3">{g.description}</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                {g.instagram_url && (
                  <a href={g.instagram_url} target="_blank" rel="noreferrer" className="neo-btn bg-cef-pink text-white text-sm py-2" data-testid={`groupe-insta-${g.id}`}>
                    <Instagram className="w-4 h-4" /> Suivre
                  </a>
                )}
                {g.contact_email && (
                  <a href={`mailto:${g.contact_email}`} className="neo-btn bg-white text-sm py-2" data-testid={`groupe-mail-${g.id}`}>
                    <Mail className="w-4 h-4" /> Écrire
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {!user && (
        <div className="mt-8 neo-card p-5 bg-cef-yellow" data-testid="login-prompt">
          🔒 Connecte-toi pour créer ta propre action et débloquer le badge initiative.
          <Link to="/login?next=/carte" className="ml-3 font-bold underline">Se connecter →</Link>
        </div>
      )}

      {showGroupForm && user && (
        <SuggestGroupModal
          user={user}
          onClose={() => setShowGroupForm(false)}
          onSubmitted={() => { setShowGroupForm(false); load(); }}
        />
      )}
    </div>
  );
}

function AgendaCard({ item, joined, onToggleJoin, onFly, canParticipate }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="neo-card p-4 bg-white" data-testid={`agenda-item-${item.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="block w-full text-left" data-testid={`agenda-toggle-${item.id}`}>
        <div className="flex items-start justify-between gap-2">
          <span className="chip" style={{ background: TYPE_COLORS[item.type], color: item.type === "blocage" ? "#000" : "#fff" }}>{TYPE_LABELS[item.type]}</span>
          <ChevronDown className={`w-5 h-5 transition ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </div>
        <h3 className="mt-2 font-display font-black text-base tracking-tight">{item.name}</h3>
        <div className="mt-1 text-xs text-neutral-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {[item.school, item.city].filter(Boolean).join(" · ")}</div>
        {item.date && <div className="mt-1 text-xs text-neutral-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(item.date).toLocaleDateString("fr-BE", { year: "numeric", month: "long", day: "numeric" })}</div>}
        {!open && <p className="mt-2 text-sm text-neutral-700 line-clamp-2">{item.description}</p>}
      </button>

      {open && (
        <div className="mt-3 pt-3 border-t-2 border-black/10" data-testid={`agenda-detail-${item.id}`}>
          <p className="text-sm whitespace-pre-wrap text-neutral-800">{item.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={onToggleJoin}
              className={`neo-btn text-sm py-2 ${joined ? "bg-cef-green" : "bg-cef-pink text-white"}`}
              data-testid={`participate-${item.id}`}
              disabled={!canParticipate}
              title={canParticipate ? "" : "Connecte-toi pour participer"}
            >
              {joined ? <><Check className="w-4 h-4" /> Tu participes</> : <><Users className="w-4 h-4" /> Participer</>}
            </button>
            {item.contact && (
              <a
                href={`mailto:${item.contact}?subject=${encodeURIComponent(`[Élève ta voix] À propos de "${item.name}"`)}`}
                className="neo-btn bg-white text-sm py-2"
                data-testid={`contact-creator-${item.id}`}
              >
                <Mail className="w-4 h-4" /> Contacter
              </a>
            )}
            {item.lat && (
              <button onClick={onFly} className="neo-btn bg-cef-yellow text-sm py-2" data-testid={`flyto-${item.id}`}>
                <MapPin className="w-4 h-4" /> Voir sur la carte
              </button>
            )}
            {!canParticipate && (
              <Link to="/login?next=/carte" className="text-xs text-neutral-600 underline self-center" data-testid={`participate-login-${item.id}`}>
                Connecte-toi pour participer
              </Link>
            )}
          </div>
          <div className="mt-3 pt-2 border-t border-black/10 flex justify-end">
            <ReportButton targetType="initiative" targetId={item.id} testId={`report-initiative-${item.id}`} />
          </div>
        </div>
      )}
    </div>
  );
}

function CreateInitiativeForm({ user, onClose, onCreated }) {
  const [form, setForm] = React.useState({
    name: "",
    school: user?.school_name || user?.school || "",
    city: user?.school_commune || user?.city || "",
    province: user?.province || "Bruxelles-Capitale",
    address: "",
    description: "",
    contact: user?.email || "",
    type: "rassemblement",
    date: "",
    website: "",
  });
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address && !form.city) return toast.error("Donne au moins une adresse ou une commune pour géolocaliser.");
    setSubmitting(true);
    try {
      const payload = { ...form, date: form.date || null };
      const { data } = await api.post("/initiatives", payload);
      toast.success("Action publiée sur la carte ! 🎉");
      onCreated(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur — vérifie les champs.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 neo-card p-5 grid sm:grid-cols-2 gap-3 bg-white" data-testid="initiative-form">
      <div className="sm:col-span-2 flex items-center justify-between">
        <div className="font-display font-black text-lg uppercase tracking-tight">Nouvelle action</div>
        <button type="button" onClick={onClose} className="w-7 h-7 inline-flex items-center justify-center border-2 border-black rounded-md hover:bg-cef-yellow"><X className="w-4 h-4" /></button>
      </div>
      <input className="neo-input" placeholder="Nom de l'action *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="init-name" />
      <select className="neo-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="init-type">
        {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <input className="neo-input" placeholder="Ton école (optionnel)" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} data-testid="init-school" />
      <select className="neo-input" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="init-province">
        {PROVINCES.map((p) => <option key={p}>{p}</option>)}
      </select>
      <input className="neo-input" placeholder="Commune / ville *" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required data-testid="init-city" />
      <input className="neo-input" placeholder="Adresse précise (rue, n°) — pour la carte" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="init-address" />
      <input className="neo-input" type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="init-date" />
      <input className="neo-input" placeholder="Contact (email)" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="init-contact" />
      <textarea className="neo-input sm:col-span-2 min-h-[100px]" placeholder="Décris ton action — objectif, qui peut rejoindre, horaire…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="init-desc" />
      <div className="sm:col-span-2 text-xs text-neutral-600">📍 L'adresse sera automatiquement géolocalisée.</div>
      {/* Honeypot */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
        value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
        className="hidden" />
      <button type="submit" disabled={submitting} className="neo-btn bg-cef-blue text-white sm:col-span-2 disabled:opacity-50" data-testid="init-submit">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Géolocalisation…</> : "Publier sur la carte"}
      </button>
    </form>
  );
}

function SuggestGroupModal({ user, onClose, onSubmitted }) {
  const [form, setForm] = React.useState({
    name: "",
    instagram_url: "",
    province: user?.province || "Bruxelles-Capitale",
    city: "",
    school: "",
    address: "",
    description: "",
    contact_email: user?.email || "",
    website: "",
  });
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/local-groups/submit", form);
      toast.success("Merci ! Ton groupe a été soumis et sera publié après validation admin.");
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur — vérifie les champs.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="suggest-group-modal" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-2 border-black rounded-md shadow-brutal w-full max-w-xl p-6 grid sm:grid-cols-2 gap-3 my-8"
        data-testid="suggest-group-form"
      >
        <div className="sm:col-span-2 flex items-center justify-between">
          <div className="font-display font-black text-xl uppercase tracking-tight">Suggérer un groupe local</div>
          <button type="button" onClick={onClose} className="w-8 h-8 inline-flex items-center justify-center border-2 border-black rounded-md hover:bg-cef-yellow" data-testid="close-suggest-modal"><X className="w-4 h-4" /></button>
        </div>
        <p className="sm:col-span-2 text-sm text-neutral-600">Ton groupe sera publié sur la carte et dans la liste après validation par un·e administrateur·rice.</p>
        <input className="neo-input sm:col-span-2" placeholder="Nom du groupe *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="sg-name" />
        <input className="neo-input" placeholder="Lien Instagram (https://…)" value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} data-testid="sg-instagram" />
        <input className="neo-input" placeholder="Email de contact" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="sg-email" />
        <select className="neo-input" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="sg-province">
          {PROVINCES.map((p) => <option key={p}>{p}</option>)}
        </select>
        <input className="neo-input" placeholder="Ville *" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required data-testid="sg-city" />
        <input className="neo-input sm:col-span-2" placeholder="École liée (optionnel)" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} data-testid="sg-school" />
        <input className="neo-input sm:col-span-2" placeholder="Adresse précise (pour la carte)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="sg-address" />
        <textarea className="neo-input sm:col-span-2 min-h-[80px]" placeholder="Description courte *" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="sg-description" />
        {/* Honeypot */}
        <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
          value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
          className="hidden" />
        <button type="submit" disabled={submitting} className="neo-btn bg-cef-pink text-white sm:col-span-2 disabled:opacity-50" data-testid="sg-submit">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : "Soumettre pour validation"}
        </button>
      </form>
    </div>
  );
}

function SchoolAutocomplete({ value, onChange }) {
  const [results, setResults] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState(value);

  React.useEffect(() => { setQ(value); }, [value]);

  React.useEffect(() => {
    if (q.length < 3) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/schools", { params: { q, limit: 10 } }).then((r) => setResults(r.data)).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
        <input
          className="neo-input pl-9 w-full"
          placeholder="École…"
          value={q}
          onChange={(e) => { setQ(e.target.value); onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          data-testid="filter-school"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border-2 border-black rounded-md shadow-brutal max-h-60 overflow-y-auto" data-testid="school-autocomplete">
          {results.map((s) => (
            <button
              type="button"
              key={s.school_id}
              onClick={() => { onChange(s.nom_ecole); setQ(s.nom_ecole); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-cef-yellow border-b border-black/10 last:border-none"
              data-testid={`school-opt-${s.school_id}`}
            >
              <div className="font-bold">{s.nom_ecole}</div>
              <div className="text-xs text-neutral-500">{s.commune} · {s.code_postal} · {s.province}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
